import React, { useState, useEffect } from "react";
import { TradeInputTable } from "@/components/tools/lot-calculator/TradeInputTable";
import type { TradeInput } from "@/components/tools/lot-calculator/TradeInputTable";
import { useUI } from "@/contexts/UIContext";

interface LotCalculatorFormProps {
  onCalculate: (formData: any, trades: any[]) => void;
}

const defaultFormData = {
  challengeType: "2-step",
  drawdownType: "balance",
  accountSize: 100000,
  startingBalance: 100000,
  currentBalance: 100000,
  leverage: 30,
  dailyDrawdownPercent: 5,
  maxDrawdownPercent: 10,
  riskType: "percent",
  riskValue: 1,
};

const defaultTrade: TradeInput = {
  pair: '',
  direction: 'buy',
  entryPrice: '',
  stopLossPrice: '',
  takeProfitPrice: '',
};

export const LotCalculatorForm: React.FC<LotCalculatorFormProps> = ({ onCalculate }) => {
  const { setLoading, showToast } = useUI();
  const [formData, setFormData] = useState(defaultFormData);
  const [trades, setTrades] = useState<TradeInput[]>([{ ...defaultTrade }]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Add keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Ctrl/Cmd + Enter to calculate
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        handleSubmit(new Event('submit') as any);
      }
      // Ctrl/Cmd + R to reset
      if ((e.ctrlKey || e.metaKey) && e.key === 'r') {
        e.preventDefault();
        handleReset();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  const validateField = (name: string, value: any): string => {
    switch (name) {
      case 'accountSize':
      case 'startingBalance':
      case 'currentBalance':
        return value <= 0 ? 'Value must be greater than 0' : '';
      case 'leverage':
        return value <= 0 ? 'Leverage must be greater than 0' : '';
      case 'dailyDrawdownPercent':
      case 'maxDrawdownPercent':
        return value <= 0 || value > 100 ? 'Must be between 0 and 100' : '';
      case 'riskValue':
        if (formData.riskType === 'percent') {
          return value <= 0 || value > 100 ? 'Must be between 0 and 100' : '';
        }
        return value <= 0 ? 'Risk value must be greater than 0' : '';
      default:
        return '';
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const numericFields = ['accountSize', 'startingBalance', 'currentBalance', 'leverage', 'dailyDrawdownPercent', 'maxDrawdownPercent', 'riskValue'];
    const newValue = numericFields.includes(name) ? Number(value) : value;
    
    const error = validateField(name, newValue);
    setErrors(prev => ({ ...prev, [name]: error }));
    
    setFormData(prev => ({ ...prev, [name]: newValue }));
  };

  const handleTradesUpdate = (updatedTrades: any[]) => {
    setTrades(updatedTrades);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate all fields
    const newErrors: Record<string, string> = {};
    Object.entries(formData).forEach(([key, value]) => {
      const error = validateField(key, value);
      if (error) newErrors[key] = error;
    });

    // Validate trades
    const hasIncompleteTrade = trades.some(t => !t.pair || !t.entryPrice || !t.stopLossPrice);
    if (hasIncompleteTrade) {
      newErrors.trades = 'Please fill in all required trade fields (Pair, Entry, Stop Loss)';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      showToast('error', 'Please fix the errors before calculating');
      return;
    }

    try {
      setLoading(true);
      await onCalculate(formData, trades);
      showToast('success', 'Calculation completed successfully');
    } catch (error) {
      showToast('error', 'An error occurred during calculation');
      console.error('Calculation error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData(defaultFormData);
    setTrades([{ ...defaultTrade }]);
    setErrors({});
    showToast('info', 'Form has been reset');
  };

  const renderTooltip = (text: string) => (
    <div className="group relative inline-block">
      <span className="text-gray-400 cursor-help">ⓘ</span>
      <div className="hidden group-hover:block absolute z-10 p-2 bg-gray-800 text-white text-sm rounded shadow-lg w-48 -left-24 top-6">
        {text}
      </div>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-6 md:space-y-8 max-w-3xl mx-auto px-3 md:px-4">
      {/* Main Settings Section */}
      <div className="bg-[#1a1a1a] rounded-xl p-4 md:p-6 space-y-4">
        <h3 className="text-lg font-semibold text-neutral-200 mb-4">Account Settings</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="block text-sm font-medium text-neutral-300">
              Challenge Type
              {renderTooltip("Select the type of prop firm challenge you're participating in")}
            </label>
            <select 
              name="challengeType" 
              value={formData.challengeType} 
              onChange={handleChange}
              className={`w-full border rounded p-2.5 text-sm md:text-base ${errors.challengeType ? 'border-red-500' : 'border-neutral-600'}`}
            >
              <option value="instant">Instant Funding</option>
              <option value="1-step">1-Step Challenge</option>
              <option value="2-step">2-Step Challenge</option>
              <option value="3-step">3-Step Challenge</option>
            </select>
            {errors.challengeType && <p className="text-red-500 text-xs md:text-sm mt-1">{errors.challengeType}</p>}
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-medium text-neutral-300">
              Drawdown Type
              {renderTooltip("Choose between balance-based or equity-based drawdown calculation")}
            </label>
            <select 
              name="drawdownType" 
              value={formData.drawdownType} 
              onChange={handleChange}
              className={`w-full border rounded p-2.5 text-sm md:text-base ${errors.drawdownType ? 'border-red-500' : 'border-neutral-600'}`}
            >
              <option value="balance">Balance-Based</option>
              <option value="equity">Equity-Based</option>
            </select>
            {errors.drawdownType && <p className="text-red-500 text-xs md:text-sm mt-1">{errors.drawdownType}</p>}
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-medium text-neutral-300">
              Account Size ($)
              {renderTooltip("Your prop firm account size in USD")}
            </label>
            <input 
              type="number" 
              name="accountSize" 
              value={formData.accountSize} 
              onChange={handleChange}
              className={`w-full border rounded p-2.5 text-sm md:text-base ${errors.accountSize ? 'border-red-500' : 'border-neutral-600'}`}
            />
            {errors.accountSize && <p className="text-red-500 text-xs md:text-sm mt-1">{errors.accountSize}</p>}
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-medium text-neutral-300">
              Starting Balance ($)
              {renderTooltip("Your initial account balance")}
            </label>
            <input 
              type="number" 
              name="startingBalance" 
              value={formData.startingBalance} 
              onChange={handleChange}
              className={`w-full border rounded p-2.5 text-sm md:text-base ${errors.startingBalance ? 'border-red-500' : 'border-neutral-600'}`}
            />
            {errors.startingBalance && <p className="text-red-500 text-xs md:text-sm mt-1">{errors.startingBalance}</p>}
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-medium text-neutral-300">
              Current Balance ($)
              {renderTooltip("Your current account balance")}
            </label>
            <input 
              type="number" 
              name="currentBalance" 
              value={formData.currentBalance} 
              onChange={handleChange}
              className={`w-full border rounded p-2.5 text-sm md:text-base ${errors.currentBalance ? 'border-red-500' : 'border-neutral-600'}`}
            />
            {errors.currentBalance && <p className="text-red-500 text-xs md:text-sm mt-1">{errors.currentBalance}</p>}
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-medium text-neutral-300">
              Leverage
              {renderTooltip("Your account leverage (e.g., 30 for 1:30)")}
            </label>
            <input 
              type="number" 
              name="leverage" 
              value={formData.leverage} 
              onChange={handleChange}
              className={`w-full border rounded p-2.5 text-sm md:text-base ${errors.leverage ? 'border-red-500' : 'border-neutral-600'}`}
            />
            {errors.leverage && <p className="text-red-500 text-xs md:text-sm mt-1">{errors.leverage}</p>}
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-medium text-neutral-300">
              Daily Drawdown Limit (%)
              {renderTooltip("Maximum allowed daily drawdown percentage")}
            </label>
            <input 
              type="number" 
              name="dailyDrawdownPercent" 
              value={formData.dailyDrawdownPercent} 
              onChange={handleChange}
              className={`w-full border rounded p-2.5 text-sm md:text-base ${errors.dailyDrawdownPercent ? 'border-red-500' : 'border-neutral-600'}`}
            />
            {errors.dailyDrawdownPercent && <p className="text-red-500 text-xs md:text-sm mt-1">{errors.dailyDrawdownPercent}</p>}
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-medium text-neutral-300">
              Max Drawdown Limit (%)
              {renderTooltip("Maximum allowed total drawdown percentage")}
            </label>
            <input 
              type="number" 
              name="maxDrawdownPercent" 
              value={formData.maxDrawdownPercent} 
              onChange={handleChange}
              className={`w-full border rounded p-2.5 text-sm md:text-base ${errors.maxDrawdownPercent ? 'border-red-500' : 'border-neutral-600'}`}
            />
            {errors.maxDrawdownPercent && <p className="text-red-500 text-xs md:text-sm mt-1">{errors.maxDrawdownPercent}</p>}
          </div>
        </div>
      </div>

      {/* Risk Management Section */}
      <div className="bg-[#1a1a1a] rounded-xl p-4 md:p-6 space-y-4">
        <h3 className="text-lg font-semibold text-neutral-200 mb-4">Risk Management</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="block text-sm font-medium text-neutral-300">
              Risk Per Trade
              {renderTooltip("Amount of risk per trade in percentage or fixed amount")}
            </label>
            <input
              type="number"
              name="riskValue"
              min={0.01}
              step={0.01}
              value={formData.riskValue}
              onChange={handleChange}
              className={`w-full border rounded p-2.5 text-sm md:text-base bg-neutral-800 text-white ${errors.riskValue ? 'border-red-500' : 'border-neutral-600'}`}
            />
          </div>
          <div className="space-y-1">
            <label className="block text-sm font-medium text-neutral-300">
              Mode
              {renderTooltip("Choose between percentage of account or fixed dollar amount")}
            </label>
            <select
              name="riskType"
              value={formData.riskType}
              onChange={handleChange}
              className="w-full border rounded p-2.5 text-sm md:text-base bg-neutral-800 text-white border-neutral-600"
            >
              <option value="percent">% of Account</option>
              <option value="fixed">$ Fixed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Trade Input Section */}
      <div className="bg-[#1a1a1a] rounded-xl p-4 md:p-6 space-y-4">
        <h3 className="text-lg font-semibold text-neutral-200 mb-4">Trade Details</h3>
        <TradeInputTable trades={trades} onUpdate={handleTradesUpdate} />
        {errors.trades && <p className="text-red-500 text-xs md:text-sm mt-2">{errors.trades}</p>}
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row justify-center gap-3 md:gap-4 mt-6">
        <button 
          type="button" 
          onClick={handleReset}
          className="w-full sm:w-auto bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2.5 px-6 rounded-lg transition-colors text-sm md:text-base"
          title="Reset form (Ctrl/Cmd + R)"
        >
          Reset Form
        </button>
        <button 
          type="submit" 
          className="w-full sm:w-auto bg-yellow-400 hover:bg-yellow-500 text-black font-semibold py-2.5 px-6 rounded-lg transition-colors text-sm md:text-base"
          title="Calculate (Ctrl/Cmd + Enter)"
        >
          Calculate
        </button>
      </div>

      <div className="text-center text-xs md:text-sm text-neutral-500 dark:text-neutral-400 mt-4">
        <p>Keyboard shortcuts: Ctrl/Cmd + Enter to calculate, Ctrl/Cmd + R to reset</p>
      </div>
    </form>
  );
};

export default LotCalculatorForm;
